import { PageShell } from '@/components/layout/PageShell';
import { APP_NAME, CREDITS } from '@/lib/constants';
import { sectionNumber } from '@/lib/utils';

export function TermsPage() {
  return (
    <PageShell>
      <article className="mx-auto max-w-3xl pb-24 pt-8">
        <p className="marginalia">
          <span className="mr-2">{sectionNumber(1)}</span>
          terms and conditions
        </p>
        <h1 className="mt-4 font-serif text-display-lg text-ink">
          Terms of use.
        </h1>
        <p className="mt-4 font-mono text-xs text-graphite">
          Effective on first use of this service.
        </p>

        <div className="mt-12 h-px bg-ink/20" />

        <div className="prose-editorial mt-10 space-y-10 text-base leading-relaxed text-ink">
          <section>
            <h2 className="font-serif text-2xl italic text-ink">
              1. &nbsp;Nature of this service.
            </h2>
            <p className="mt-3 text-graphite">
              {APP_NAME} is a research prototype by the named authors, offered
              for academic and evaluation purposes. It is not a commercial
              product. Features, availability, output quality, and uptime are
              all subject to research priorities and may change without
              notice.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl italic text-ink">
              2. &nbsp;Who may use {APP_NAME}.
            </h2>
            <p className="mt-3 text-graphite">
              Access is granted to invited participants, collaborators, and
              peer reviewers. Accounts are provisioned via self sign-up
              using email and password. Only one session per account is
              permitted at a time; attempting to sign in from a second device
              while already signed in will return an error.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl italic text-ink">
              3. &nbsp;Your content.
            </h2>
            <p className="mt-3 text-graphite">
              You retain ownership of every sketch, questionnaire response,
              design asset, hi-fi image, and code file you provide or receive
              through {APP_NAME}. You grant the authors a limited, research-only
              licence to process your uploads through the agent pipeline and
              to retain derived telemetry for the duration of the study
              and up to two years post-publication, after which the
              identifiable data is deleted. See the{' '}
              <a
                href="/privacy"
                className="underline decoration-ink/30 underline-offset-4 hover:decoration-ochre hover:text-ochre"
              >
                Privacy Policy
              </a>{' '}
              for detail.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl italic text-ink">
              4. &nbsp;What you must not do.
            </h2>
            <p className="mt-3 text-graphite">
              Do not upload content you are not authorised to share. Do not
              upload private, sensitive, or personally identifying images of
              other people. Do not attempt to reverse-engineer the agent
              prompts, bypass the single-session control, or overwhelm the
              system with automated traffic. Do not use {APP_NAME} to produce
              interfaces for illegal, harassing, or deceptive purposes.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl italic text-ink">
              5. &nbsp;Outputs and responsibility.
            </h2>
            <p className="mt-3 text-graphite">
              AI-generated outputs \u2014 hi-fi mockups and generated code \u2014
              are delivered as-is. They may contain errors, visual artefacts,
              or accessibility gaps that the validator agent did not flag
              within its iteration budget. You are responsible for reviewing
              outputs before deploying them, and for the downstream use of
              any generated code. {APP_NAME} makes no warranty as to fitness
              for any particular purpose.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl italic text-ink">
              6. &nbsp;Third-party models.
            </h2>
            <p className="mt-3 text-graphite">
              The pipeline calls models hosted by Anthropic (Claude) and
              Google (Gemini). Your uploads and questionnaire answers are
              transmitted to those providers as part of normal inference.
              Their respective terms and privacy policies apply in addition
              to ours.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl italic text-ink">
              7. &nbsp;Termination.
            </h2>
            <p className="mt-3 text-graphite">
              The authors may suspend or terminate any account that violates
              these terms, without prior notice. You may delete your account
              at any time; on deletion, your projects, uploads, and derived
              artefacts are removed within thirty days.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl italic text-ink">
              8. &nbsp;Contact.
            </h2>
            <p className="mt-3 text-graphite">
              Questions, takedown requests, and research enquiries should be
              directed to{' '}
              {CREDITS.map((c, i) => (
                <span key={c.url}>
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="underline decoration-ink/30 underline-offset-4 hover:decoration-ochre hover:text-ochre"
                  >
                    {c.name}
                  </a>
                  {i < CREDITS.length - 1 ? ' or ' : '.'}
                </span>
              ))}
            </p>
          </section>
        </div>
      </article>
    </PageShell>
  );
}
